import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Page, 
  Layout, 
  Banner,
  Toast,
  PageActions,
  Box,
  Spinner,
  Text,
  BlockStack,
  Tabs,
  Card,
  Button
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { SettingsForm } from '../components/SettingsForm';
import { WidgetCustomizer } from '../components/WidgetCustomizer';
import { RecommendationWeightsForm } from '../components/RecommendationWeightsForm';
import { SessionScoringForm } from '../components/SessionScoringForm';
import { PopupSettingsForm } from '../components/PopupSettingsForm';
import { CartDrawerSettingsForm } from '../components/CartDrawerSettingsForm';
import { useAuthenticatedFetch } from '../hooks/useApi';
import { 
  DisplaySettings, 
  AISettings, 
  DataCollectionSettings, 
  WidgetCustomization, 
  RecommendationWeights,
  SessionScoringSettings,
  MerchantPopupSettings,
  CartDrawerSettings,
  defaultWidgetCustomization,
  defaultRecommendationWeights,
  defaultSessionScoring,
  defaultPopupSettings,
  defaultCartDrawerSettings
} from '../types';

const defaultDisplaySettings: DisplaySettings = {
  showOnProductPage: true,
  showOnCartPage: true,
  showOnHomePage: false,
  maxRecommendations: 4,
  widgetTitle: 'You might also like',
  widgetStyle: 'grid',
  recommendationType: 'similar',
  cartWidgetEnabled: false,
  cartWidgetTitle: 'Complete your purchase',
  cartWidgetType: 'frequently-bought',
  showLowStockIndicator: true,
  lowStockThreshold: 5,
  lowStockText: 'Only {count} left!',
};

const defaultAISettings: AISettings = {
  strategy: 'hybrid',
  minConfidence: 0.5,
  enablePersonalization: true,
};

const defaultDataCollectionSettings: DataCollectionSettings = {
  trackProductViews: true,
  trackAddToCart: true,
  trackPurchases: true,
  trackSearches: true,
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop, settings, refreshSettings, isLoading } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();
  const [searchParams] = useSearchParams();

  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>(
    settings?.displaySettings || defaultDisplaySettings
  );
  const [aiSettings, setAISettings] = useState<AISettings>(
    settings?.aiSettings || defaultAISettings
  );
  const [dataCollectionSettings, setDataCollectionSettings] = useState<DataCollectionSettings>(
    settings?.dataCollectionSettings || defaultDataCollectionSettings
  );
  const [widgetCustomization, setWidgetCustomization] = useState<WidgetCustomization>(
    settings?.widgetCustomization || defaultWidgetCustomization
  );
  const [recommendationWeights, setRecommendationWeights] = useState<RecommendationWeights>(
    settings?.recommendationWeights || defaultRecommendationWeights
  );
  const [sessionScoring, setSessionScoring] = useState<SessionScoringSettings>(
    settings?.sessionScoring || defaultSessionScoring
  );
  const [popupSettings, setPopupSettings] = useState<MerchantPopupSettings>(
    settings?.popupSettings || defaultPopupSettings
  );
  const [cartDrawerSettings, setCartDrawerSettings] = useState<CartDrawerSettings>(
    settings?.cartDrawerSettings || defaultCartDrawerSettings
  );
  const [selectedTab, setSelectedTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIds = ['general', 'customization', 'weights', 'bundles', 'scoring', 'popups', 'smartcart'];
      const index = tabIds.indexOf(tabParam);
      if (index !== -1) return index;
    }
    return 0;
  });

  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync local state when settings load from context (fixes race condition
  // where useState initializes with defaults before settings are fetched)
  useEffect(() => {
    if (settings) {
      setDisplaySettings(settings.displaySettings || defaultDisplaySettings);
      setAISettings(settings.aiSettings || defaultAISettings);
      setDataCollectionSettings(settings.dataCollectionSettings || defaultDataCollectionSettings);
      setWidgetCustomization(settings.widgetCustomization || defaultWidgetCustomization);
      setRecommendationWeights(settings.recommendationWeights || defaultRecommendationWeights);
      setSessionScoring(settings.sessionScoring || defaultSessionScoring);
      setPopupSettings(settings.popupSettings || defaultPopupSettings);
      setCartDrawerSettings(settings.cartDrawerSettings || defaultCartDrawerSettings);
      // Don't mark as changed – this is a load, not a user edit
      setHasChanges(false);
    }
  }, [settings]);

  const handleDisplaySettingsChange = useCallback((newSettings: DisplaySettings) => {
    setDisplaySettings(newSettings);
    setHasChanges(true);
  }, []);

  const handleAISettingsChange = useCallback((newSettings: AISettings) => {
    setAISettings(newSettings);
    setHasChanges(true);
  }, []);

  const handleDataCollectionSettingsChange = useCallback((newSettings: DataCollectionSettings) => {
    setDataCollectionSettings(newSettings);
    setHasChanges(true);
  }, []);

  const handleWidgetCustomizationChange = useCallback((newSettings: WidgetCustomization) => {
    setWidgetCustomization(newSettings);
    setHasChanges(true);
  }, []);

  const handleRecommendationWeightsChange = useCallback((newSettings: RecommendationWeights) => {
    setRecommendationWeights(newSettings);
    setHasChanges(true);
  }, []);

  const handleSessionScoringChange = useCallback((newSettings: SessionScoringSettings) => {
    setSessionScoring(newSettings);
    setHasChanges(true);
  }, []);

  const handlePopupSettingsChange = useCallback((newSettings: MerchantPopupSettings) => {
    setPopupSettings(newSettings);
    setHasChanges(true);
  }, []);

  const handleCartDrawerSettingsChange = useCallback((newSettings: CartDrawerSettings) => {
    setCartDrawerSettings(newSettings);
    setHasChanges(true);
  }, []);

  const handleSave = async () => {
    if (!shop) return;

    setIsSaving(true);
    try {
      await fetchWithAuth('/api/shopify/settings', {
        method: 'POST',
        body: {
          displaySettings,
          aiSettings,
          dataCollectionSettings,
          widgetCustomization,
          recommendationWeights,
          // bundleSettings excluded — managed via /app/bundles to avoid stale-data overwrites
          sessionScoring,
          popupSettings,
          cartDrawerSettings,
        },
      });

      setToastMessage(t('settings.savedSuccess'));
      setHasChanges(false);
      await refreshSettings();
    } catch (error) {
      setToastMessage(t('settings.saveError'));
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setDisplaySettings(settings?.displaySettings || defaultDisplaySettings);
    setAISettings(settings?.aiSettings || defaultAISettings);
    setDataCollectionSettings(settings?.dataCollectionSettings || defaultDataCollectionSettings);
    setWidgetCustomization(settings?.widgetCustomization || defaultWidgetCustomization);
    setRecommendationWeights(settings?.recommendationWeights || defaultRecommendationWeights);
    setSessionScoring(settings?.sessionScoring || defaultSessionScoring);
    setPopupSettings(settings?.popupSettings || defaultPopupSettings);
    setCartDrawerSettings(settings?.cartDrawerSettings || defaultCartDrawerSettings);
    setHasChanges(false);
  };

  const tabs = [
    { id: 'general', content: t('settings.tabs.general'), accessibilityLabel: t('settings.tabs.general') },
    { id: 'customization', content: t('settings.tabs.customization'), accessibilityLabel: t('settings.tabs.customization') },
    { id: 'weights', content: t('settings.tabs.weights'), accessibilityLabel: t('settings.tabs.weights') },
    { id: 'bundles', content: t('settings.tabs.bundles'), accessibilityLabel: t('settings.tabs.bundles') },
    { id: 'scoring', content: t('settings.tabs.scoring'), accessibilityLabel: t('settings.tabs.scoring') },
    { id: 'popups', content: t('settings.tabs.popups'), accessibilityLabel: t('settings.tabs.popups') },
    { id: 'smartcart', content: t('settings.tabs.smartCart'), accessibilityLabel: t('settings.tabs.smartCart') },
  ];

  if (!shop) {
    return (
      <Page>
        <Banner tone="warning" title={t('dashboard.errors.shopNotFound')}>
          <p>{t('dashboard.errors.shopNotFoundDesc')}</p>
        </Banner>
      </Page>
    );
  }

  if (isLoading || !settings) {
    return (
      <Page>
        <Box padding="800">
          <BlockStack gap="400" align="center">
            <Spinner size="large" />
            <Text as="p">{t('settings.loadingSettings')}</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  return (
    <Page
      title={t('settings.title')}
      backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
        primaryAction={{
          content: t('common.save'),
          onAction: handleSave,
          loading: isSaving,
          disabled: !hasChanges,
        }}
      >
        <Layout>
          {hasChanges && (
            <Layout.Section>
              <Banner
                title={t('common.unsavedChanges', 'Unsaved changes')}
                action={{ content: t('common.save'), onAction: handleSave }}
                secondaryAction={{ content: t('common.discard', 'Discard'), onAction: handleDiscard }}
              >
                <p>{t('settings.unsavedChanges')}</p>
              </Banner>
            </Layout.Section>
          )}

          <Layout.Section>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
              <Box paddingBlockStart="400">
                {selectedTab === 0 && (
                  <SettingsForm
                    displaySettings={displaySettings}
                    aiSettings={aiSettings}
                    dataCollectionSettings={dataCollectionSettings}
                    onDisplaySettingsChange={handleDisplaySettingsChange}
                    onAISettingsChange={handleAISettingsChange}
                    onDataCollectionSettingsChange={handleDataCollectionSettingsChange}
                  />
                )}
                {selectedTab === 1 && (
                  <WidgetCustomizer
                    customization={widgetCustomization}
                    onChange={handleWidgetCustomizationChange}
                    widgetTitle={displaySettings.widgetTitle}
                    widgetStyle={displaySettings.widgetStyle}
                  />
                )}
                {selectedTab === 2 && (
                  <RecommendationWeightsForm
                    weights={recommendationWeights}
                    onChange={handleRecommendationWeightsChange}
                  />
                )}
                {selectedTab === 3 && (
                  <Card>
                    <BlockStack gap="400">
                      <Text as="h2" variant="headingMd">{t('settings.tabs.bundles')}</Text>
                      <Text as="p" tone="subdued">
                        {t('bundles.manageBundlesDesc', 'Bundle settings and management have been moved to a dedicated page for a better experience.')}
                      </Text>
                      <Box>
                        <Button variant="primary" onClick={() => navigate('/app/bundles')}>
                          {t('bundles.goToBundles', 'Go to Bundles')}
                        </Button>
                      </Box>
                    </BlockStack>
                  </Card>
                )}
                {selectedTab === 4 && (
                  <SessionScoringForm
                    settings={sessionScoring}
                    onChange={handleSessionScoringChange}
                  />
                )}
                {selectedTab === 5 && (
                  <PopupSettingsForm
                    settings={popupSettings}
                    onChange={handlePopupSettingsChange}
                  />
                )}
                {selectedTab === 6 && (
                  <CartDrawerSettingsForm
                    settings={cartDrawerSettings}
                    onChange={handleCartDrawerSettingsChange}
                  />
                )}
              </Box>
            </Tabs>
          </Layout.Section>

          <Layout.Section>
            <PageActions
              primaryAction={{
                content: t('settings.saveSettings', 'Save settings'),
                onAction: handleSave,
                loading: isSaving,
                disabled: !hasChanges,
              }}
              secondaryActions={[
                {
                  content: t('settings.discardChanges', 'Discard changes'),
                  onAction: handleDiscard,
                  disabled: !hasChanges,
                },
              ]}
            />
          </Layout.Section>
        </Layout>

      {toastMessage && (
        <Toast content={toastMessage} onDismiss={() => setToastMessage(null)} />
      )}
    </Page>
  );
}
